import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from 'react-modal';
import { Download, Info } from 'react-feather';

import { downloadFile, createFileUrl } from 'utils/appUtils';
import { Button, Loading } from 'components/standalone';
import IndividualTicket from '../IndividualTicket/IndividualTicket';
import PurchasesService from 'services/PurchasesService';
import * as S from './styles';

const customStyles = {
	overlay: {
		zIndex: 9999,
		backgroundColor: 'rgba(0, 0, 0, 0.25)',
		overflow: 'auto',
	},
	content : {
		top: '100px',
		left: '50%',
		right: 'auto',
		bottom: 'auto',
		marginRight: '-50%',
		transform: 'translateX(-50%)',
		maxWidth: '600px',
		width: '90%',
	},
};

interface Ticket {
	attendeeCode: string;
	attendeeFirstName: string;
	attendeeLastName: string;
	ticketBatch: string;
	ticketName: string;
	ticketPrice: number;
	transferable?: boolean;
	transferStatus?: string;
	transferId?: number;
	transferDirection?: string;
	transferredToName?: string;
	transferredFromName?: string;
	transferDate?: string;
	refundable?: boolean;
}

interface EventData {
	name: string;
	startDate: string;
	customDate: string;
	fullAddress: string;
	locationType: string;
	thumbnail: string;
}

interface PurchaseModalProps {
	isOpen: boolean;
	isOrder: boolean;
	eventName: string;
	id: string;
	eventId: number | null;
	event: EventData | null;
	closeModalFn: () => void;
}

const PurchaseModal = ({ isOpen, isOrder, eventName, id, eventId, event, closeModalFn }: PurchaseModalProps) => {
	const [ tickets, setTickets ] = useState([]);
	const [ isLoading, setIsLoading ] = useState(true);
	const [ isDownloadingAll, setIsDownloadingAll ] = useState(false);

	useEffect(() => {
		if (id) {
			setIsLoading(true);
			getTickets();
		}
	}, [id])

	const getTickets = () => {
		PurchasesService.getPurchaseTickets(id)
			.then(({ data }: any) => {
				setIsLoading(false);
				setTickets(data.content);
			})
			.catch(({ data }: any) => {
				closeModalFn();
				toast(data.detail, { type: toast.TYPE.ERROR });
			})
	}

	const downloadAllTickets = () => {
		if (!eventId) return;
		setIsDownloadingAll(true);
		PurchasesService.downloadAllTicket(eventId, Number(id))
			.then(({ data }: any) => downloadFile(createFileUrl(data), 'ingressos'))
			.catch((error: any) => {
				const reader = new FileReader();
				reader.onload = () => {
					try {
						const errorMessage = JSON.parse(reader.result as string);
						toast(errorMessage.detail, { type: toast.TYPE.ERROR });
					} catch {
						toast('Erro ao baixar ingressos', { type: toast.TYPE.ERROR });
					}
				};
				reader.readAsText(error.data);
			})
			.finally(() => setIsDownloadingAll(false));
	}

	const hasTransferred = tickets.some((ticket: Ticket) => ticket.transferStatus === 'TRANSFERRED');
	const hasDownloadableTicket = tickets.some((ticket: Ticket) => ticket.transferStatus !== 'TRANSFERRED');

	return (
		<Modal
			isOpen={isOpen}
			style={customStyles}
			ariaHideApp={false}
			onAfterOpen={() => document.body.style.overflow = 'hidden'}
			onAfterClose={() => document.body.style.overflow = 'unset'}>
			<S.Title>Ingressos - {eventName}</S.Title>

			{isLoading
				? <Loading text='Buscando seus ingressos' height='200px' />
				: tickets.map((ticket: Ticket) => (
					<IndividualTicket
						key={ticket.attendeeCode}
						ticket={ticket}
						event={event}
						onTransferred={getTickets}
					/>
				))
			}

			{!isLoading && hasTransferred && (
				<S.RefundNote>
					<Info size={15} />
					Ingressos transferidos não podem ser reembolsados.
				</S.RefundNote>
			)}

			<S.Footer>
				{isOrder && eventId && hasDownloadableTicket && (
					<S.DownloadAllButton onClick={downloadAllTickets} disabled={isDownloadingAll || isLoading}>
						<Download size={16} />
						{isDownloadingAll ? 'Baixando...' : 'Baixar todos (PDF)'}
					</S.DownloadAllButton>
				)}
				<Button handleClick={closeModalFn}>Fechar</Button>
			</S.Footer>
		</Modal>
	);
}

export default PurchaseModal;
